import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import '../../styles/UpgradePromptModal.css';

/**
 * UpgradePromptModal Component
 *
 * Modal shown when user attempts an action that exceeds their plan limits.
 * Prompts user to upgrade their subscription.
 */
const UpgradePromptModal = ({
    isOpen,
    onClose,
    currentOwners,
    maxOwners,
    planName,
    suggestedPlanName,
    availableUpgrades
}) => {
    const navigate = useNavigate();

    if (!isOpen) return null;

    const handleUpgradeClick = () => {
        onClose();
        navigate('/subscription/plans');
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="upgrade-modal-overlay" onClick={handleOverlayClick}>
            <div className="upgrade-modal">
                <div className="upgrade-modal-header">
                    <div className="upgrade-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2>Osiągnięto limit właścicieli</h2>
                </div>

                <div className="upgrade-modal-body">
                    <p className="limit-info">
                        Twój plan <strong>{planName}</strong> pozwala na maksymalnie{' '}
                        <strong>{maxOwners}</strong> właścicieli.
                    </p>
                    <p className="current-usage">
                        Aktualnie masz <strong>{currentOwners}</strong> z{' '}
                        <strong>{maxOwners}</strong> właścicieli.
                    </p>

                    <div className="upgrade-suggestion">
                        <p>Aby dodać więcej właścicieli, ulepsz swój plan.</p>
                        {suggestedPlanName && (
                            <p className="suggested-plan">
                                Polecamy: <strong>{suggestedPlanName}</strong>
                            </p>
                        )}
                    </div>

                    {availableUpgrades && availableUpgrades.length > 0 && (
                        <div className="available-upgrades">
                            <p className="upgrades-title">Dostępne plany:</p>
                            <ul>
                                {availableUpgrades.map((plan) => (
                                    <li key={plan.code}>
                                        <span className="plan-upgrade-name">{plan.name}</span>
                                        <span className="plan-upgrade-details">
                                            {plan.maxOwners ? `do ${plan.maxOwners} właścicieli` : 'bez limitu'} - {plan.monthlyPrice}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="upgrade-modal-footer">
                    <button className="btn-secondary" onClick={onClose}>
                        Anuluj
                    </button>
                    <button className="btn-primary" onClick={handleUpgradeClick}>
                        Zobacz plany
                    </button>
                </div>
            </div>
        </div>
    );
};

UpgradePromptModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    currentOwners: PropTypes.number.isRequired,
    maxOwners: PropTypes.number.isRequired,
    planName: PropTypes.string.isRequired,
    suggestedPlanName: PropTypes.string,
    availableUpgrades: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            maxOwners: PropTypes.number,
            monthlyPrice: PropTypes.string
        })
    )
};

UpgradePromptModal.defaultProps = {
    suggestedPlanName: null,
    availableUpgrades: null
};

export default UpgradePromptModal;
